import {FC, JSX, useMemo} from 'react';
import {styled} from 'styled-components';

import {Markdown} from '@shared/dynamo_model';

import {EmptyFragment} from '@shared-frontend/lib/react';

interface MarkdownRendererProps {
  markdown: Markdown;
}

interface MarkdownElementText {
  type: 'text';
  value: string;
}

interface MarkdownElementBoldText {
  type: 'bold';
  value: MarkdownElement[];
}

interface MarkdownElementTitle1 {
  type: 'title1';
  value: MarkdownElement[];
}

interface MarkdownElementTitle2 {
  type: 'title2';
  value: MarkdownElement[];
}

interface MarkdownElementSeparator {
  type: 'separator';
}

type MarkdownElement =
  | MarkdownElementText
  | MarkdownElementBoldText
  | MarkdownElementTitle1
  | MarkdownElementTitle2
  | MarkdownElementSeparator;

function parseMarkdownLine(str: string): MarkdownElement[] {
  if (str.startsWith('# ')) {
    return [{type: 'title1', value: parseMarkdownLine(str.slice(2))}];
  } else if (str.startsWith('## ')) {
    return [{type: 'title2', value: parseMarkdownLine(str.slice(2))}];
  } else if (str === '---') {
    return [{type: 'separator'}];
  }
  const elements: MarkdownElement[] = [];
  let scannedText = '';
  // Loop through the characters until we find special chars
  for (let i = 0; i < str.length; i++) {
    const c1 = str[i];
    const c2 = str[i + 1];
    // "**<text>**"
    // Detect start
    if (c1 === '*' && c2 === '*') {
      let boldContent = '';
      for (let j = i + 2; j < str.length; j++) {
        const j1 = str[j];
        const j2 = str[j + 1];
        // Detect end
        if (j1 === '*' && j2 === '*') {
          if (scannedText.length > 0) {
            elements.push({type: 'text', value: scannedText});
          }
          elements.push({type: 'bold', value: parseMarkdownLine(boldContent)});
          const rest = str.slice(j + 2);
          if (rest.length > 0) {
            elements.push(...parseMarkdownLine(rest));
          }
          return elements;
        }
        boldContent += j1;
      }
    }
    scannedText += c1;
  }
  return [{type: 'text', value: scannedText}];
}

function parseMarkdown(markdown: Markdown): MarkdownElement[][] {
  return markdown.split('\n').map(parseMarkdownLine);
}

function renderElement(element: MarkdownElement, key?: string | number): JSX.Element | string {
  if (element.type === 'title1') {
    return <Title1 key={key}>{element.value.map(renderElement)}</Title1>;
  } else if (element.type === 'title2') {
    return <Title2 key={key}>{element.value.map(renderElement)}</Title2>;
  } else if (element.type === 'bold') {
    return <BoldText key={key}>{element.value.map(renderElement)}</BoldText>;
  } else if (element.type === 'text') {
    return element.value;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (element.type === 'separator') {
    return <Separator key={key} />;
  }
  return EmptyFragment;
}

export const MarkdownRenderer: FC<MarkdownRendererProps> = props => {
  const {markdown} = props;
  const markdownElements = useMemo(() => parseMarkdown(markdown), [markdown]);

  return (
    <Wrapper>
      {markdownElements.map((elements, i) => {
        const [firstElement] = elements;
        if (firstElement && firstElement.type !== 'text' && elements.length === 1) {
          return renderElement(firstElement, i);
        }
        return (
          <Line key={i} data-key={i}>
            {elements.map(renderElement)}
          </Line>
        );
      })}
    </Wrapper>
  );
};
MarkdownRenderer.displayName = 'MarkdownRenderer';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  width: 100%;
`;

const Line = styled.div`
  display: inline-block;
  min-height: 24px;
  line-height: 24px;
`;

const Title1 = styled.div`
  font-size: 24px;
  font-weight: bold;
  line-height: 34px;
  margin-bottom: 6px;
`;
const Title2 = styled.div`
  font-size: 20px;
  font-weight: bold;
  line-height: 29px;
  margin-bottom: 4px;
`;
const BoldText = styled.span`
  font-weight: bold;
`;
const Separator = styled.div`
  height: 1px;
  background-color: #00000033;
`;
